/**
 * Comments attached to a single photo.
 *
 * The same `comments` table backs these and the ones under a wall post — the
 * schema models the two as mutually exclusive nullable columns, so the only
 * difference here is which id is sent. Rendered inside the viewer's details
 * panel, where gestures are already suspended and a text field cannot be
 * mistaken for something to swipe.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "../../lib/trpc.js";
import { Avatar } from "../../components/ui/Avatar.js";
import { Button } from "../../components/ui/Button.js";
import { useToast } from "../../components/ui/Toast.js";
import { relativeTime, exactTime } from "../../lib/relativeTime.js";
import type { Comment } from "./types.js";
import "./PhotoComments.css";

export interface PhotoCommentsProps {
  groupId: string;
  fileId: string;
  canComment: boolean;
}

export function PhotoComments({
  groupId,
  fileId,
  canComment,
}: PhotoCommentsProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [draft, setDraft] = useState("");

  const key = ["photo-comments", groupId, fileId];

  const comments = useQuery({
    queryKey: key,
    queryFn: () =>
      trpcClient.social.comments.list.query({ groupId, postId: null, fileId }),
  });

  const add = useMutation({
    mutationFn: (body: string) =>
      trpcClient.social.comments.create.mutate({
        groupId,
        postId: null,
        fileId,
        body,
      }),
    onSuccess: () => {
      setDraft("");
      void queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (error) => toast(error.message, { variant: "danger" }),
  });

  const submit = () => {
    const body = draft.trim();
    if (body.length > 0) add.mutate(body);
  };

  const items = (comments.data ?? []) as unknown as Comment[];

  return (
    <section className="fc-photo-comments" aria-label="Comments on this photo">
      <h3>Comments</h3>

      {comments.isLoading ? (
        <p className="fc-photo-comments__empty">Loading…</p>
      ) : comments.isError ? (
        // Distinguished from "none yet" on purpose: a failed fetch that renders
        // as an empty thread invites someone to repeat a comment that is
        // already there.
        <p className="fc-photo-comments__error">
          Couldn&apos;t load comments.{" "}
          <button type="button" onClick={() => void comments.refetch()}>
            Try again
          </button>
        </p>
      ) : items.length === 0 ? (
        <p className="fc-photo-comments__empty">
          Be the first to say something.
        </p>
      ) : (
        <ul className="fc-photo-comments__list">
          {items.map((comment) => (
            <li key={comment.id} className="fc-photo-comment">
              <Avatar
                name={comment.author?.displayName ?? "?"}
                size="sm"
                decorative
              />
              <div className="fc-photo-comment__body">
                <span className="fc-photo-comment__author">
                  {comment.author?.displayName ?? "Someone who left"}
                </span>
                <span className="fc-photo-comment__text">{comment.body}</span>
                <time
                  className="fc-photo-comment__time"
                  dateTime={comment.createdAt}
                  title={exactTime(comment.createdAt)}
                >
                  {relativeTime(comment.createdAt)}
                </time>
              </div>
            </li>
          ))}
        </ul>
      )}

      {canComment && (
        <div className="fc-photo-comments__compose">
          <textarea
            className="fc-photo-comments__input"
            rows={1}
            value={draft}
            maxLength={2000}
            placeholder="Add a comment…"
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                submit();
              }
              // The viewer closes on Escape and navigates on the arrows. While
              // the caret is in here those keys belong to the text field.
              event.stopPropagation();
            }}
          />
          <Button
            variant="secondary"
            size="sm"
            loading={add.isPending}
            disabled={draft.trim().length === 0}
            onClick={submit}
          >
            Send
          </Button>
        </div>
      )}
    </section>
  );
}
